import { CreditCard, Ellipsis, Eye, LogOut, Pencil, ShieldCheck, SlidersVertical } from "lucide-react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router";
import type { MembershipDto, MeResponse } from "../../shared/api-types";
import { api } from "../api/client";
import { openPreview, useAppearance, useMenus, useProfile } from "../api/queries";
import { useAuth } from "../auth/AuthContext";
import { PopMenu, useFocusRescue } from "../components/kit";
import { NotificationBell, type NotificationList } from "../components/NotificationBell";
import { ICON, Logo } from "../components/ui";
import { initialsOf, plural } from "../lib/format";
import { ImportProgressDock } from "./import/ImportProgressDock";
import { mainMenu } from "./menus/status";
import { MOBILE_TABS, NAV, NAV_FLAT } from "./nav";
import { AllergenBanner } from "./nutrition/AllergenBanner";
import { RestaurantSwitcher } from "./RestaurantSwitcher";
import { TrialBanner } from "./settings/TrialBanner";
import { SupportChat } from "./support/SupportPage";

const MOBILE_LABEL: Record<string, string> = {
  overview: "Acasă",
  products: "Meniu",
  import: "Import",
  qr: "QR",
};

export interface ShellContext {
  me: MeResponse;
  membership: MembershipDto;
}

const ROLE_LABEL = { owner: "Proprietar", manager: "Manager", staff: "Personal" } as const;

export function AppShell({ me, membership }: ShellContext) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const restaurant = membership.restaurant;
  const menus = useMenus(restaurant.id);
  const profile = useProfile(restaurant.id);
  const appearance = useAppearance(restaurant.id);
  useFocusRescue();
  const inMobileTabs = NAV_FLAT.filter((i) => (MOBILE_TABS as readonly string[]).includes(i.id)).some((i) =>
    i.path === "/app" ? pathname === "/app" : pathname.startsWith(i.path),
  );

  // Global draft indicator (prototype `pendingTotal`): menu changes of published menus + appearance changes +
  // public profile changes.
  const list = menus.data ?? [];
  const pendingMenus = list.filter((m) => !m.archived && m.publishedVersionId && (m.pendingChanges ?? 0) > 0);
  const menuPending = pendingMenus.reduce((a, m) => a + (m.pendingChanges ?? 0), 0);
  const profilePending = (profile.data?.changes.length ?? 0) > 0 ? 1 : 0;
  const appearancePending = appearance.data?.changes.length ?? 0;
  const pendingTotal = menuPending + appearancePending + profilePending;
  const live = list.some((m) => m.publicVisible && !m.archived);
  const main = mainMenu(list);

  async function onLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  function openPending() {
    const first = pendingMenus[0];
    if (first) navigate(`/app/products?menu=${first.id}&publish=1`);
    else if (appearancePending) navigate("/app/appearance");
    else navigate("/app/settings");
  }

  return (
    <div className="shell">
      <aside className="sidebar" aria-label="Navigare principală">
        <Logo className="sidebar-logo" white />
        <RestaurantSwitcher me={me} current={restaurant.id} variant="card" />
        <nav className="stack" style={{ gap: 20 }}>
          {NAV.map((group) => (
            <div key={group.label}>
              <h2 className="nav-group-label">{group.label}</h2>
              <ul className="nav-list">
                {group.items.map((item) => (
                  <li key={item.id}>
                    <NavLink to={item.path} end={item.path === "/app"} className="nav-item">
                      <item.icon {...ICON} aria-hidden="true" />
                      <span style={{ flex: 1 }}>{item.label}</span>
                      {item.id === "products" && menuPending > 0 && (
                        <span className="pending-pill" style={{ height: 22, padding: "0 8px", fontSize: 12 }}>
                          {menuPending}
                        </span>
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
      </aside>
      <div className="main-col">
        <header className="topbar">
          <Logo className="topbar-logo" white />
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
            <RestaurantSwitcher me={me} current={restaurant.id} />
            <div className="topbar-sub desktop-only">
              {menus.isPending ? "" : live ? "meniu publicat" : "niciun meniu publicat"}
            </div>
          </div>
          {pendingTotal > 0 && (
            <button type="button" className="pending-pill" onClick={openPending}>
              <Pencil size={18} strokeWidth={1.75} aria-hidden="true" />
              <span className="desktop-only">
                {plural(pendingTotal, "modificare nepublicată", "modificări nepublicate")}
              </span>
              <span className="mobile-only">{pendingTotal}</span>
            </button>
          )}
          <NotificationBell
            scope={restaurant.id}
            load={() => api<NotificationList>("GET", `/restaurants/${restaurant.id}/notifications`)}
            markRead={() => api<void>("POST", `/restaurants/${restaurant.id}/notifications/read`)}
          />
          <button
            type="button"
            className="btn btn-secondary btn-sm desktop-only"
            disabled={!main}
            onClick={() => openPreview(restaurant.id, main?.id)}
          >
            <Eye {...ICON} aria-hidden="true" />
            Previzualizează
          </button>
          <div className="desktop-only">
            <PopMenu
              label={`Contul meu: ${me.user.name}`}
              triggerClass="avatar"
              trigger={initialsOf(me.user.name)}
              header={
                <div className="pop-head">
                  <span style={{ fontWeight: 600 }}>{me.user.name}</span>
                  <span className="caption muted" style={{ fontWeight: 400, overflowWrap: "anywhere" }}>
                    {ROLE_LABEL[membership.role]} · {me.user.email}
                  </span>
                </div>
              }
              items={[
                { label: "Setări", icon: SlidersVertical, onSelect: () => navigate("/app/settings") },
                {
                  label: "Abonament și facturare",
                  icon: CreditCard,
                  onSelect: () => navigate("/app/billing"),
                },
                { label: "Deconectare", icon: LogOut, onSelect: onLogout },
              ]}
            />
          </div>
        </header>
        <main className="main" id="main" tabIndex={-1}>
          <div className="main-inner">
            {me.support && <SupportModeBanner expiresAt={me.support.expiresAt} onExit={onLogout} />}
            {membership.role === "owner" && !me.support && (
              <TrialBanner
                rid={restaurant.id}
                live={live}
                onBillingPage={pathname.startsWith("/app/billing")}
              />
            )}
            <AllergenBanner
              rid={restaurant.id}
              hidden={pathname === "/app" || pathname.startsWith("/app/nutrition")}
            />
            <Outlet context={{ me, membership } satisfies ShellContext} />
          </div>
        </main>
      </div>
      <ImportProgressDock rid={restaurant.id} />
      {!me.support && <SupportChat rid={restaurant.id} />}
      <nav className="bottom-nav" aria-label="Navigare principală">
        {MOBILE_TABS.map((id) => {
          const item = NAV_FLAT.find((i) => i.id === id);
          if (!item) return null;
          return (
            <NavLink key={id} to={item.path} end={item.path === "/app"}>
              <item.icon size={22} strokeWidth={1.75} aria-hidden="true" />
              {MOBILE_LABEL[id]}
            </NavLink>
          );
        })}
        <NavLink to="/app/more" aria-current={!inMobileTabs ? "page" : undefined}>
          <Ellipsis size={22} strokeWidth={1.75} aria-hidden="true" />
          Mai mult
        </NavLink>
      </nav>
    </div>
  );
}

/**
 * Support mode (owner 2026-11-03): the Meniova team inside the restaurant's account, opened from the admin for one
 * hour. Changes are made as the owner would and recorded under the team member's name; billing, team, data and the
 * chat are off. „Ieși” ends the session and closes the tab the admin opened.
 */
function SupportModeBanner({ expiresAt, onExit }: { expiresAt: number; onExit: () => Promise<void> }) {
  const until = new Date(expiresAt).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" });
  return (
    <div className="support-mode" role="status">
      <ShieldCheck {...ICON} aria-hidden="true" />
      <span style={{ flex: 1 }}>
        <strong>Mod suport Meniova.</strong> Lucrezi în contul restaurantului până la {until}. Modificările se
        salvează ca la proprietar și apar în istoric pe numele tău. Plățile, echipa și datele contului nu se
        pot schimba.
      </span>
      <button
        type="button"
        className="btn btn-secondary btn-sm"
        onClick={async () => {
          await onExit();
          window.close();
        }}
      >
        Ieși din cont
      </button>
    </div>
  );
}
